import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

/*  ÜZEMSZÜNET-FIGYELŐ – közös állapotszolgáltatás.
 *
 *  A külső rendszerek (NAV, Ügyfélkapu/KAÜ, bank-aggregátor, e-Önkormányzat) tervezett vagy
 *  váratlan kiesését jelezzük a felhasználónak. A funkciót NEM tiltjuk, csak figyelmeztetünk.
 *
 *  Egyetlen helyen töltjük be a `GET /api/uzemszunet/allapot`-ot, és rövid ideig cache-eljük,
 *  hogy több banner (NAV, bank, tárhely…) egyszerre NE indítson külön-külön lekérést. A
 *  szerviz-kártya `frissit=true`-val mindig friss adatot kér. */

export type UzemszunetForrasKulcs = 'nav' | 'ugyfelkapu' | 'aggreg8' | 'eonkormanyzat';
export type UzemszunetAllapotKod = 'ok' | 'tervezett' | 'kieses';
export type UzemszunetForrasTipus = 'auto' | 'futas' | 'email' | 'kezi';

export interface UzemszunetForras {
  forras: UzemszunetForrasKulcs;
  nev: string;
  allapot: UzemszunetAllapotKod;
  uzenet?: string;
  idoablak_kezd?: string | null;
  idoablak_veg?: string | null;
  forras_tipus?: UzemszunetForrasTipus;
  frissitve?: string;
}

export interface UzemszunetAllapot {
  forrasok: UzemszunetForras[];
  van_aktiv: boolean;
}

/** A kézi felülírás kérés törzse (állapot + üzenet + időablak). */
export interface UzemszunetKeziKeres {
  allapot: UzemszunetAllapotKod;
  uzenet: string;
  idoablak_kezd: string | null;
  idoablak_veg: string | null;
}

@Injectable({ providedIn: 'root' })
export class UzemszunetService {
  /** A legutóbbi (megosztott) lekérés – a bannerek ezen osztoznak. */
  private cache?: Promise<UzemszunetAllapot>;
  private cacheIdo = 0;
  /** A cache élettartama: egy percen belül a bannerek ugyanazt az adatot kapják. */
  private readonly ttl = 60000;

  constructor(private http: HttpClient) {}

  /** A teljes állapot. Hiba esetén üres (nem borítja a hívó oldalt). `frissit`=true kihagyja a cache-t. */
  allapot(frissit = false): Promise<UzemszunetAllapot> {
    const most = Date.now();
    if (frissit || !this.cache || (most - this.cacheIdo) > this.ttl) {
      this.cacheIdo = most;
      this.cache = firstValueFrom(this.http.get<UzemszunetAllapot>('/api/uzemszunet/allapot'))
        .then((v) => ({ forrasok: Array.isArray(v?.forrasok) ? v.forrasok : [], van_aktiv: !!v?.van_aktiv }))
        .catch((): UzemszunetAllapot => ({ forrasok: [], van_aktiv: false }));
    }
    return this.cache;
  }

  /** Egy forrás állapota (vagy null, ha nincs ilyen / nem jött adat). */
  async forras(kulcs: string, frissit = false): Promise<UzemszunetForras | null> {
    const a = await this.allapot(frissit);
    return (a.forrasok || []).find((f) => f.forras === kulcs) || null;
  }

  /** Kézi felülírás mentése (admin – a jogot a kiszolgáló ellenőrzi). */
  async keziMent(kulcs: string, keres: UzemszunetKeziKeres): Promise<any> {
    const v = await firstValueFrom(this.http.put(`/api/uzemszunet/kezi/${kulcs}`, keres));
    this.ervenytelenit();
    return v;
  }

  /** Kézi felülírás törlése (admin). */
  async keziTorol(kulcs: string): Promise<any> {
    const v = await firstValueFrom(this.http.delete(`/api/uzemszunet/kezi/${kulcs}`));
    this.ervenytelenit();
    return v;
  }

  /** A cache eldobása, hogy a következő lekérés friss legyen (pl. mentés után). */
  ervenytelenit(): void { this.cache = undefined; this.cacheIdo = 0; }
}

/*  Megjelenik-e a figyelmeztetés? Akkor, ha az időablak még AKTUÁLIS vagy JÖVŐBELI – azaz nincs
 *  lejárt záró idő. (Záró idő nélkül nyitott, tartós állapotnak vesszük.) */
export function aktivVagyJovobeli(f: UzemszunetForras | null): boolean {
  if (!f) return false;
  if (!f.idoablak_veg) return true;
  const veg = new Date(f.idoablak_veg).getTime();
  if (isNaN(veg)) return true;
  return veg >= Date.now();
}

/** KÖZELGŐ (még nem kezdődött el): van kezdő időpont, és az a JÖVŐBEN van – előre jelzett karbantartás. */
export function kozelgo(f: UzemszunetForras | null): boolean {
  if (!f || !f.idoablak_kezd) return false;
  const kezd = new Date(f.idoablak_kezd).getTime();
  return !isNaN(kezd) && kezd > Date.now();
}

/** Emberi időablak-szöveg, pl. „2026.10.26 14:00 – 16:00”. Üres, ha nincs megadva. */
export function idoablakSzoveg(kezd?: string | null, veg?: string | null): string {
  const k = datumSzoveg(kezd);
  const v = datumSzoveg(veg);
  if (k && v) return `${k} – ${v}`;
  if (k) return `${k}-tól`;
  if (v) return `${v}-ig`;
  return '';
}

/** Egy ISO időpont magyaros, rövid szöveges alakja (üres, ha nincs/érvénytelen). */
export function datumSzoveg(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const p = (n: number) => (n < 10 ? '0' : '') + n;
  return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}
